import React, { Suspense, lazy } from 'react';
import { DEMO_CONFIG } from '../../config/contract';

// Lazy load components
const ShopPanel = lazy(() => import('../shop/ShopPanel').then(module => ({ default: module.ShopPanel })));
const StatsPanel = lazy(() => import('../StatsPanel').then(module => ({ default: module.StatsPanel })));
const AchievementsPanel = lazy(() => import('../achievements/AchievementsPanel').then(module => ({ default: module.AchievementsPanel })));
const FortuneWheel = lazy(() => import('../game/FortuneWheel').then(module => ({ default: module.FortuneWheel })));

interface ModalsContainerProps {
  isShopOpen: boolean;
  isStatsOpen: boolean;
  isAchievementsOpen: boolean;
  isFortuneWheelOpen: boolean;
  gameState: any;
  wheelSegments: any[];
  spinCost: number;
  autoClickerActive: boolean;
  onCloseShop: () => void;
  onCloseStats: () => void;
  onCloseAchievements: () => void;
  onCloseFortuneWheel: () => void;
  onToggleAutoClicker: () => void;
  onPurchase: (newBalance: number) => void;
  walletAddress: string | null;
}

export function ModalsContainer({
  isShopOpen,
  isStatsOpen,
  isAchievementsOpen,
  isFortuneWheelOpen,
  gameState,
  wheelSegments,
  spinCost,
  autoClickerActive,
  onCloseShop,
  onCloseStats,
  onCloseAchievements,
  onCloseFortuneWheel,
  onToggleAutoClicker,
  onPurchase,
  walletAddress
}: ModalsContainerProps) {
  return (
    <Suspense fallback={<div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 backdrop-blur-sm" />}>
      {isShopOpen && (
        <ShopPanel
          isOpen={isShopOpen}
          onClose={onCloseShop}
          balance={gameState.balance}
          autoClickerActive={autoClickerActive}
          onToggleAutoClicker={onToggleAutoClicker}
          onPurchase={onPurchase}
          walletAddress={walletAddress}
        />
      )}

      {isStatsOpen && (
        <StatsPanel
          isOpen={isStatsOpen}
          onClose={onCloseStats}
          stats={{
            minimumWithdrawal: DEMO_CONFIG.minWithdrawal,
            maximumWithdrawal: DEMO_CONFIG.maxWithdrawal,
            coinsPerWei: 100,
            weiPerSmallEth: 1000000000000000,
            weiPerEth: 1000000000000000000,
            maxWithdrawalPerDay: DEMO_CONFIG.maxDailyWithdrawal,
            maxWithdrawalPerWeek: DEMO_CONFIG.maxWeeklyWithdrawal,
            maxWithdrawalPerMonth: DEMO_CONFIG.maxMonthlyWithdrawal,
            multipliersBought: gameState.multiplierCount || 0,
            autoClickersBought: gameState.autoClickerCount,
            totalSpentOnMultipliers: gameState.totalSpentMultipliers,
            totalSpentOnAutoClickers: gameState.totalSpentAutoClickers,
            totalNetworkFees: 0,
            totalNetworkFeesWei: 0,
            totalEarnedFromMultipliers: 0,
            totalEarnedFromAutoClickers: 0,
          }}
        />
      )}

      {isAchievementsOpen && (
        <AchievementsPanel
          isOpen={isAchievementsOpen}
          onClose={onCloseAchievements}
          walletAddress={walletAddress}
        />
      )}

      {isFortuneWheelOpen && (
        <FortuneWheel
          segments={wheelSegments}
          spinCost={spinCost}
          onClose={onCloseFortuneWheel}
          walletAddress={walletAddress}
        />
      )}
    </Suspense>
  );
}